import { BaseIcon } from "@/shared/ui/BaseIcons";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import type { TheHeaderProps } from "../model/types";

type Props = TheHeaderProps & {
  onMobileSidebarOpen: () => void;
};

export const TheHeader: React.FC<Props> = (props) => {
  const [userConfigPopoverAnchor, setUserConfigPopoverAnchor] =
    useState<null | HTMLElement>(null);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        zIndex: (theme) => ({
          xs: "auto",
          sm: theme.zIndex.drawer + 1,
        }),
        backgroundColor: "white",
        borderBottom: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Toolbar sx={{ paddingRight: 0 }}>
        <Stack direction="row" alignItems="center" width="100%">
          {/* ロゴ画像 */}
          <Stack direction="row" alignItems="flex-end">
            <Box
              component="img"
              src="/logipoke-connect-logo.svg"
              aria-label="Logo"
              alt="logo"
              sx={{
                height: { xs: "24px", sm: "24px" },
              }}
            />
          </Stack>

          <Box flex={1} />

          {/* 会社名・ユーザー名 */}
          {props.user.isAuthenticated ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-end", sm: "center" }}
              spacing={{ xs: 0, sm: 2 }}
              sx={{
                overflow: "hidden",
                paddingRight: { xs: 2, sm: 0 },
                paddingLeft: { xs: 2, sm: 0 },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "grey.700",
                  fontSize: { xs: "10px", sm: "14px" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {props.user.companyName}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{
                  backgroundColor: "white",
                  display: { xs: "none", sm: "block" },
                }}
              />

              {/* デスクトップ用ユーザー名 */}
              <Button
                variant="text"
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  setUserConfigPopoverAnchor(
                    userConfigPopoverAnchor ? null : event.currentTarget,
                  );
                }}
                sx={{ display: { xs: "none", sm: "block" }, paddingLeft: 0 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <BaseIcon
                    name="user"
                    htmlColor="#344540"
                    sx={{ fontSize: "18px" }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: "grey.700",
                      fontSize: "14px",
                    }}
                  >
                    {props.user.name}
                  </Typography>
                  <Popover
                    open={userConfigPopoverAnchor !== null}
                    anchorEl={userConfigPopoverAnchor}
                    onClose={() => setUserConfigPopoverAnchor(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    sx={{
                      display: { xs: "none", sm: "block" },
                      marginTop: "8px",
                    }}
                  >
                    <Stack alignItems="start" px={2} py={1}>
                      <Button variant="text" onClick={props.onLogoutClick}>
                        ログアウト
                      </Button>
                    </Stack>
                  </Popover>
                </Stack>
              </Button>

              {/* モバイル用ユーザー名 */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  display: { xs: "flex", sm: "none" },
                  maxWidth: "100%",
                }}
              >
                <BaseIcon
                  name="user"
                  htmlColor="#344540"
                  sx={{ fontSize: "12px" }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: "grey.700",
                    fontSize: "10px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {props.user.name}
                </Typography>
              </Stack>
            </Stack>
          ) : (
            // FYI: 仕様上はログイン画面に遷移するのでこの状態でヘッダーが表示されることはない
            <Button variant="text" onClick={props.onLoginClick}>
              ログインする
            </Button>
          )}

          {/* モバイル用・サイドバーのトリガー */}
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
            }}
          >
            <Divider
              orientation="vertical"
              variant="fullWidth"
              flexItem
              sx={{
                backgroundColor: "white",
              }}
            />
            <IconButton
              sx={{
                width: "56px",
                height: "56px",
              }}
              onClick={props.onMobileSidebarOpen}
            >
              <MenuIcon sx={{ fontSize: "24px", color: "primary.400" }} />
            </IconButton>
          </Box>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
